import { FC, useEffect } from "react";

import Button from "components/Button";
import InfoCard from "components/InfoCard";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { removeAssetRequest, resetSuccessfullyRemovedAsset } from "store/slices/assetSlice";
import { hideModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";

import styles from "../style.module.scss";

type PropTypes = {
  onClose?: () => void;
  id?: number;
  name?: string;
};

const ModalRemoveAssetContent: FC<PropTypes> = ({ onClose, id, name }) => {
  const dispatch = useAppDispatch();
  const { isLoading, successfullyRemovedId } = useAppSelector((state: RootStore) => state.assets);

  const handleDelete = () => {
    if (id === undefined) {
      return;
    }
    dispatch(removeAssetRequest(Number(id)));
  };

  const handleCancel = () => {
    onClose?.();
    dispatch(hideModal());
  };

  useEffect(() => {
    if (successfullyRemovedId) {
      dispatch(hideModal());
      dispatch(resetSuccessfullyRemovedAsset());
      dispatch(
        showToast({
          id: "successfully-removed-asset",
          message: "The asset was successfully deleted.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyRemovedId, dispatch]);

  return (
    <div className={styles.content}>
      <p className={styles.contentTitle}>
        Are you sure you want to delete the asset <span>"{name}"</span>?
      </p>

      <InfoCard color="red" variant="red" size="small">
        <p className={styles.contentInfoTitle}>Warning: This asset contains data</p>

        <ul className={styles.contentInfoList}>
          <li>All associated devices and incidents will be removed</li>
          <li>All historical data will be permanently lost</li>
        </ul>
      </InfoCard>

      <p>This action cannot be undone. All associated data will be permanently deleted.</p>

      <div className={styles.buttons}>
        <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>

        <Button variant="primary" onClick={handleDelete} disabled={isLoading}>
          Delete
        </Button>
      </div>
    </div>
  );
};

export default ModalRemoveAssetContent;
